/*
 * File: PageSubHeader.tsx
 * Project: rosita
 * -----
 * Last Modified: 18 Feb 2022 4:38:40 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { skipToken } from '@reduxjs/toolkit/dist/query';
import { Col, Menu, Row, Space, Spin } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { InheritanceSelector } from 'components/atoms/InheritanceSelector';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGetBusinessQuery, useGetDivisionQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { setDataAreaId } from 'redux/services/donaldGrump/releasedProductsParams';
import { useAppDispatch } from 'redux/store';

const styles: InlineStylesModel = {
  row: {
    backgroundColor: '#181823',
    padding: '0 50px',
    color: '#84859a',
    fontSize: '1rem',
    maxWidth: 1920,
    margin: '0 auto'
  },
  menuContainer: {
    height: '100%'
  },
  row2: {
    width: '100%',
    maxWidth: 1920
  },
  smallContainer: {
    width: '100%'
  },
  largeContainer: {}
};

export const PageSubHeader = (): JSX.Element => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const businessId = pathname.includes('/business/') && pathname.split('/')[2];
  const divisionId = pathname.includes('/division/') && pathname.split('/')[2];

  let customerName = '';

  /* ******************** API Hooks ******************** */
  const { data: divisionData, isFetching: isDivisionFetching } = useGetDivisionQuery(divisionId || skipToken);
  const { data: businessData, isFetching: isBusinessFetching } = useGetBusinessQuery(businessId || skipToken);

  if (divisionData && !isDivisionFetching && divisionId) {
    customerName = `Division - ${divisionData?.erpId} | ${divisionData?.name}`;
  } else if (businessData && !isBusinessFetching && businessId) {
    customerName = `Business - ${businessData?.erpId} | ${businessData?.name}`;
  } else {
    customerName = '';
  }

  const menuJSX = customerName ? (
    <Menu disabledOverflow={true} mode="horizontal" theme="dark" style={styles.menuContainer}>
      <Menu.Item key="customerSelection">
        <Link to="/">Customer Selection</Link>
      </Menu.Item>
    </Menu>
  ) : null;

  useEffect(() => {
    if (divisionData && !isDivisionFetching && divisionId) dispatch(setDataAreaId(divisionData.business.dataAreaId));
    if (businessData && !isBusinessFetching && businessId) dispatch(setDataAreaId(businessData?.dataAreaId));
    if (!customerName && !isDivisionFetching && !isBusinessFetching) dispatch(setDataAreaId(undefined));
  }, [businessData, businessId, customerName, dispatch, divisionData, divisionId, isBusinessFetching, isDivisionFetching]);

  return (
    <div style={{ width: '100%', backgroundColor: 'rgb(24, 24, 35)' }}>
      <Row style={styles.row} justify="space-between" align="middle">
        <Col>
          <Space size="large">
            <Spin spinning={isDivisionFetching || isBusinessFetching} indicator={<BlockLoader direction="loader loader--slideUp" />}>
              <div>{customerName}</div>
            </Spin>
            {pathname.includes('/division/') && <InheritanceSelector />}
          </Space>
        </Col>
        <Col>{menuJSX}</Col>
      </Row>
    </div>
  );
};
